/**
 * Área FICHA · ABAS HABILITADAS: quais seções de dados valem para os produtos do lead aberto.
 *
 * É a `abasHabilitadasParaProdutos` do legado (`admin.html:6147` a `6156`), usada por DUAS abas: a
 * Leitura Estratégica (`6184`, `6302`) e o Dossiê (`5563`, `5583`). Mora num módulo próprio para as
 * duas lerem da mesma consulta, como no legado, que também tem uma função só para as duas.
 *
 * A VISIBILIDADE DAS ABAS na moldura (`aplicarVisibilidadeAbas`, `6086`) faz uma consulta parecida,
 * com a coluna `ordem` a mais, e é da moldura, e não daqui.
 */

// Tudo que uma área usa vem do núcleo, e de mais lugar nenhum (ver PADRAO.md).
import { clienteDe, executar, sucesso, type FonteDeDados, type LinhaParametrosProdutoAba, type Resultado } from '../nucleo.js';

/** A coluna que a consulta pede: só `aba`. */
export type AbaHabilitada = Pick<LinhaParametrosProdutoAba, 'aba'>;

/**
 * As visibilidades que contam para o admin: a aba vale quando é de "ambos" ou só da
 * "administradora" (`admin.html:6154`). A de "cliente" fica de fora, porque é só do portal dela.
 */
const VISIBILIDADES_DO_ADMIN = ['ambos', 'administradora'] as const;

/**
 * Lê as abas vinculadas a uma lista de produtos, já na chave de parâmetro
 * (`estrategia_imagem_pessoal_profissional`, e não `eip`).
 * Legado: `admin.html:6148` a `6155`, `sb.from('parametros_produto_aba').select('aba')
 * .in('produto', paramKeys).in('visibilidade', ['ambos', 'administradora'])`.
 *
 * SEM PRODUTO, NENHUMA CONSULTA: o legado devolve o conjunto vazio antes de ir ao banco
 * (`6149`), e a lista vazia aqui é esse mesmo conjunto vazio.
 */
export async function lerAbasHabilitadas(
  fonte: FonteDeDados,
  produtosParam: readonly string[],
): Promise<Resultado<readonly AbaHabilitada[]>> {
  // O mesmo atalho do legado: nada a filtrar, nada a pedir.
  if (produtosParam.length === 0) return sucesso<readonly AbaHabilitada[]>([]);
  // A consulta do legado, com os dois filtros na mesma ordem.
  const resultado = await executar<readonly AbaHabilitada[] | null>(
    clienteDe(fonte)
      .from('parametros_produto_aba')
      .select('aba')
      .in('produto', [...produtosParam])
      .in('visibilidade', [...VISIBILIDADES_DO_ADMIN]),
  );
  // Resposta sem linhas vira lista vazia, como o `(data || [])` do legado.
  return resultado.ok ? sucesso(resultado.dados ?? []) : resultado;
}
